<script setup lang="ts">
import { h, ref, computed, onBeforeMount, onMounted, toRaw } from 'vue';
import { useI18n } from 'vue-i18n';
import cloneDeep from 'lodash/cloneDeep';
import { CRUISE_TYPE } from '@/constants';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useBookComposable } from './useBookComposable';
import { useModalComposable } from '@/views/composable';
import { useSubmitWorksheetStore } from '@/store/SubmitWorksheetStore';
import { useBookingQuoteStore } from '@/store/BookingQuoteStore';
import { useToast } from 'primevue/usetoast';
import { useDialog } from 'primevue/usedialog';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import InputMask from 'primevue/inputmask';
import InputNumber from 'primevue/inputnumber';
import TableColumn from 'primevue/column';
import CruiseOptionCabinsInfoTable from './CruiseOptionTable/CruiseOptionCabinsInfoTable.vue';
import CruiseOptionFlightPriceTable from './CruiseOptionTable/CruiseOptionFlightPriceTable.vue';
import CruiseOptionPreHotelTable from './CruiseOptionTable/CruiseOptionPreHotelTable.vue';
import CruiseOptionPostHotelTable from './CruiseOptionTable/CruiseOptionPostHotelTable.vue';
import ExportExcelFile from '@/components/modal/utils/ExportExcelFile.vue';
import LoadingSpinner from '../LoadingSpinner.vue';

interface Props {
  priorityStatus: number;
  isManagement: boolean;
  dataProducts: any;
}
const props = withDefaults(defineProps<Props>(), {
  dataProducts: {}
});

const emits = defineEmits<{ openClickSubmit: [isZero: boolean] }>();

const dialog = useDialog();
const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const adminSetupStore = useAdminSetupStore();
const bookingQuoteStore = useBookingQuoteStore();
const submitWorksheetStore = useSubmitWorksheetStore();
const { isInvalidCruiseUpdate, roomFields, hasEditOccupacyPermission, columnStyles, handleExportCruiseBooking, renderFileNameExport } = useBookComposable();
const { handleOpenErrorMessage, showMinNotification } = useModalComposable();

const cabinType = {
  inside: 'Inside',
  outside: 'Outside',
  balcony: 'Balcony',
  suite: 'Suite'
};

const data = ref();
const cruiseEditingRows = ref([]);
const expandedRows = ref<any>({});
const isShowModalBehind = ref(true);
const isLoading = ref(false);
const isShowAddPax = ref(false);
const isSubmit = ref(false);
const maxChildren = 4;
const columns = [
  { field: 'singleRoom', peopleNo: 1 },
  { field: 'doubleRoom', peopleNo: 2 },
  { field: 'tripleRoom', peopleNo: 3 },
  { field: 'quadRoom', peopleNo: 4 },
  { field: 'fiveRoom', peopleNo: 5 },
  { field: 'sixRoom', peopleNo: 6 }
];
const cabinDetailOccupanciesClone = ref<any>();
const isUpdateChildForm = ref(false);
const quote = computed(() => bookingQuoteStore.quote);
const cruiseMinimumConfig = computed(() => adminSetupStore.minimumConfig?.cruiseConfig);

const getListPassengerFromHotel = (rooms: any, target: string) => {
  if (!rooms) {
    return [];
  }
  let result = [];
  rooms.forEach((room) => {
    roomFields.forEach((field) => {
      const roomInfoList = room.roomInfo[`${field}Infos`];

      if (roomInfoList) {
        roomInfoList.forEach((roomInfo) => {
          if (roomInfo[target]) {
            const filterPassengers = roomInfo[target].filter((pas) => pas.firstName && pas.lastName);

            const formatPassengers = filterPassengers.map((item) => {
              return {
                email: roomInfo.email,
                passenger: { ...item }
              };
            });
            result = [...result, ...formatPassengers];
          }
        });
      }
    });
  });

  return result;
};

const getListPassengerFromCabins = (cabins: any, target: string) => {
  if (!cabins) {
    return [];
  }
  let result = [];
  cabins.forEach((cabin) => {
    roomFields.forEach((field) => {
      const cabinInfoList = cabin[`${field}Infos`];
      if (cabinInfoList) {
        cabinInfoList.forEach((cabinInfo) => {
          if (cabinInfo[target]) {
            const filterPassengers = cabinInfo[target].filter((pas) => pas.firstName && pas.lastName);
            const formatPassengers = filterPassengers.map((item) => {
              return {
                email: cabinInfo.email,
                passenger: { ...item }
              };
            });
            result = [...result, ...formatPassengers];
          }
        });
      }
    });
  });
  return result;
};

const getListPassengerFromFlights = (flights: any, target: string) => {
  if (!flights) {
    return [];
  }
  let result = [];
  flights.forEach((flight) => {
    flight.parties?.forEach((party) => {
      if (party[target]) {
        const filterPassengers = party[target].filter((pas) => pas.firstName && pas.lastName);
        const formatPassengers = filterPassengers.map((item) => {
          return {
            email: party.email,
            passenger: { ...item }
          };
        });
        result = [...result, ...formatPassengers];
      }
    });
  });
  return result;
};

const getListPassenger = (cruiseList: any[], target: string) => {
  let result: any = [];
  cruiseList.forEach((cruise) => {
    cruise.preHotel.forEach((option: any) => {
      option.hotels.forEach((hotel: any) => {
        result = [...result, ...getListPassengerFromHotel(hotel.rooms, target)];
      });
    });
    cruise.postHotel.forEach((option: any) => {
      option.hotels.forEach((hotel: any) => {
        result = [...result, ...getListPassengerFromHotel(hotel.rooms, target)];
      });
    });
    result = [...result, ...getListPassengerFromFlights(cruise.flightPrices, target)];
    result = [...result, ...getListPassengerFromCabins(cruise.cabinInfos, target)];
  });

  let i = 0;
  while (i !== result.length) {
    const dupplicatePassengerIndex = result.findIndex(
      (passenger, index) => index !== i && passenger.passenger.firstName === result[i].passenger.firstName && passenger.passenger.lastName === result[i].passenger.lastName && passenger.passenger.middleName === result[i].passenger.middleName
    );
    if (dupplicatePassengerIndex === -1) {
      i += 1;
    } else {
      result.splice(dupplicatePassengerIndex, 1);
    }
  }
  return result;
};

const handleTotalAdultsChildren = (occupancy: any) => {
  let totalAdults = 0;
  let totalChildren = 0;

  for (const roomType in occupancy) {
    if (occupancy[roomType]?.length) {
      occupancy[roomType]?.forEach((person: any) => {
        totalAdults += person.adults;
        totalChildren += person.children;
      });
    }
  }
  return totalAdults + totalChildren;
};

const checkChildrenInRoom = (occupancy: any) => {
  let isValid: boolean = true;
  for (const roomType in occupancy) {
    if (occupancy[roomType]?.length) {
      occupancy[roomType].forEach((person: any) => {
        if (person.children > fillChildren(roomType)) {
          isValid = false;
        }
      });
    }
  }
  return isValid;
};

const fillChildren = (field: string | undefined): number => {
  const column = columns.find((col: any) => col.field === field);
  return column ? maxChildren - column.peopleNo : 0;
};

const checkOccupancy = (data: any) => {
  const rawData = toRaw(data);
  const dataClone = cloneDeep(rawData);

  let isValid: boolean = true;
  dataClone.forEach((option: any) => {
    const { adt, chd } = option;
    const totalAllowed = adt + chd;
    let totalOccupants = 0;
    option.hotels.forEach((hotel: any) => {
      hotel.rooms.forEach((room: any) => {
        const occupancy = room.detailedOccupancy;
        totalOccupants = totalOccupants + handleTotalAdultsChildren(occupancy);
      });
    });

    if (totalOccupants > totalAllowed) {
      isValid = false;
    }
  });
  return isValid;
};

const checkCabin = (data: any) => {
  const rawData = toRaw(data);
  const dataClone = cloneDeep(rawData);
  let isValid: boolean = true;
  const totalPassengerAllowed = data.adt + data.chd;
  let totalOccupantsTotal: number = 0;
  dataClone.cabinDetailOccupancies?.forEach((cabins: any) => {
    const totalOccupants = handleTotalAdultsChildren(cabins.detailedOccupancy);
    totalOccupantsTotal = totalOccupantsTotal + totalOccupants;
    if (totalOccupantsTotal > totalPassengerAllowed) {
      isValid = false;
    }
    if (!checkChildrenInRoom(cabins.detailedOccupancy)) {
      isValid = false;
    }
  });
  return isValid;
};

const areAllRoomNumbersZeroCabin = (quote: any) => {
  for (const cruise of quote.cruises) {
    for (const cabinInfo of cruise.cabinInfos) {
      if (cabinInfo.singleNo || cabinInfo.doubleNo || cabinInfo.tripleNo || cabinInfo.quadNo || cabinInfo.fiveInRoomNo || cabinInfo.sixInRoomNo) {
        return false;
      }
    }
  }
  return true;
};

const areAllRoomNumbersZeroCruise = (cruises: any, hotelType: any) => {
  return cruises.map((cruise: any) => {
    for (const option of cruise[hotelType]) {
      for (const hotel of option.hotels) {
        for (const room of hotel.rooms) {
          const roomInfo = room.roomInfo;
          if (roomInfo.singleNo || roomInfo.doubleNo || roomInfo.tripleNo || roomInfo.quadNo || roomInfo.fiveInRoomNo || roomInfo.sixInRoomNo) {
            return false;
          }
        }
      }
    }
    return true;
  });
};

const checkZeroPrePostCabin = (payload: any, isZero: boolean) => {
  let isZeroTemp = isZero;
  const hasHotels = payload.cruises.every((cruise: any) => {
    return cruise.preHotel.length && cruise.postHotel.length;
  });
  if (hasHotels) {
    isZeroTemp = areAllRoomNumbersZeroCruise(payload.cruises, 'preHotel') && areAllRoomNumbersZeroCruise(payload.cruises, 'postHotel') && areAllRoomNumbersZeroCabin(payload);
  }
  return isZeroTemp;
};

const checkZeroPostCabin = (payload: any, isZero: boolean) => {
  let isZeroTemp = isZero;
  const noHotels = payload.cruises.every((cruise: any) => {
    return !cruise.preHotel.length && cruise.postHotel.length;
  });
  if (noHotels) {
    isZeroTemp = areAllRoomNumbersZeroCruise(payload.cruises, 'postHotel') && areAllRoomNumbersZeroCabin(payload);
  }
  return isZeroTemp;
};

const checkZeroPreCabin = (payload: any, isZero: boolean) => {
  let isZeroTemp = isZero;
  const noHotels = payload.cruises.every((cruise: any) => {
    return cruise.preHotel.length && !cruise.postHotel.length;
  });
  if (noHotels) {
    isZeroTemp = areAllRoomNumbersZeroCruise(payload.cruises, 'preHotel') && areAllRoomNumbersZeroCabin(payload);
  }
  return isZeroTemp;
};

const checkZeroOnlyCabin = (payload: any, isZero: boolean) => {
  let isZeroTemp = isZero;
  const noHotels = payload.cruises.every((cruise: any) => {
    return !cruise.preHotel.length && !cruise.postHotel.length;
  });
  if (noHotels) {
    isZeroTemp = areAllRoomNumbersZeroCabin(payload);
  }
  return isZeroTemp;
};

const submitCruiseQuote = async (updatePassengers: boolean = false, isUpdateBookingCruise: boolean = false) => {
  isSubmit.value = true;

  if (isUpdateBookingCruise && (!checkOccupancy(quote.value.cruises[0].preHotel) || !checkOccupancy(quote.value.cruises[0].postHotel) || !checkCabin(quote.value.cruises[0]))) {
    handleOpenErrorMessage(t('my_quote.toastMessage.over_selected_room'));
    return;
  }

  try {
    isLoading.value = true;
    const payload: any = {};
    payload.qType = quote.value.qType;
    payload.cruises = quote.value.cruises;
    payload.expires = quote.value.expires;
    payload.linkedQuotes = quote.value.linkedQuotes;
    if (updatePassengers) {
      if (!isUpdateBookingCruise) {
        payload.cruises[0].cabinDetailOccupancies = cabinDetailOccupanciesClone.value;
      }
      await submitWorksheetStore.updatePassengers(payload, quote.value.sk);
    } else {
      await submitWorksheetStore.handleUpdateWorkSheet(payload, quote.value.sk);
    }
    const isZero: boolean = handleCheckZero(payload);

    emits('openClickSubmit', isZero);

    if (isUpdateBookingCruise) {
      isUpdateChildForm.value = true;
      isShowAddPax.value = true;
      cabinDetailOccupanciesClone.value = cloneDeep(data.value[0].cabinDetailOccupancies);
    }
    if (!updatePassengers && !isUpdateBookingCruise) {
      isShowAddPax.value = false;
    }

    toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
  } catch {
    toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
  } finally {
    isSubmit.value = false;
    isLoading.value = false;
  }
};

const handleCheckZero = (payload: any) => {
  let isZero: boolean = true;
  isZero = checkZeroOnlyCabin(payload, isZero);
  isZero = checkZeroPreCabin(payload, isZero);
  isZero = checkZeroPostCabin(payload, isZero);
  isZero = checkZeroPrePostCabin(payload, isZero);
  return isZero;
};

const handleOnCloseModal = (dialogData: any, event: any) => {
  document.getElementsByTagName('body')[0].classList.add('p-overflow-hidden-2');
  if (dialogData.data) {
    if (event.callback) {
      return event.callback(dialogData.data, event.cruiseIndex, event.index, event.hotelType, event.indexHotel);
    }
    isShowModalBehind.value = false;
    dialogData.data.data.forEach((item: any) => {
      event.dataOption[`${item.field}RoomInfos`] = item.passengers.map((passenger: any) => {
        return {
          adtPassengers: passenger.people,
          chdPassengers: passenger.children || [],
          infPassengers: passenger.infants || [],
          email: passenger.email,
          roomRequests: passenger.roomRequests
        };
      });
    });
    submitCruiseQuote(true);
    isShowModalBehind.value = true;
  }
};

const handleDataSaveCruise = (dialogRef: any) => {
  isUpdateChildForm.value = false;
  dialogRef.close({ buttonType: 'Save', data: dialogRef.data.occupancy });
};

const addPax = (event: any, isCabin: boolean = false) => {
  const total = event?.dataOption.adtNo + event?.dataOption.chdNo + event?.dataOption.infNo;
  const dialogRef = dialog.open(event?.component, {
    data: {
      data: event?.dataOption,
      cabinOccupancy: isCabin ? event?.cabinOccupancy : undefined,
      cabinType,
      quoteId: quote.value?.sk,
      priorityStatus: props.priorityStatus,
      qType: quote.value.qType,
      isManagement: props.isManagement,
      retDate: event?.retDate,
      listPassenger: getListPassenger(quote.value.cruises, 'adtPassengers'),
      listChildren: getListPassenger(quote.value.cruises, 'chdPassengers'),
      listInfants: getListPassenger(quote.value.cruises, 'infPassengers'),
      allowCopy: true,
      gType: quote.value.gType,
      isUpdateChildForm: isUpdateChildForm.value
    },
    props: {
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true,
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      }
    },
    templates: {
      header: () => {
        return [h('div', { class: 'flex align-items-center' }, [t('booking.hint_header'), h('i', { class: 'ml-3 text-4xl fa-solid fa-hand-point-right' })])];
      },
      footer: () => {
        if (hasEditOccupacyPermission) {
          return [
            h(Button, {
              label: t('common.save'),
              class: 'mt-4',
              icon: 'pi pi-check',
              autofocus: true,
              disabled: total == 0,
              onClick: () => {
                handleDataSaveCruise(dialogRef);
              }
            })
          ];
        }
      }
    },
    onClose: (dialogData: any) => {
      handleOnCloseModal(dialogData, event);
    }
  });
};

const formatBookingId = (bookingIds: string) => {
  if (bookingIds) {
    return bookingIds.replace(/,/g, '\n');
  }
};

const onRowEditSaveCruise = (event: any) => {
  const cabinTemp = data.value[event.index];
  const isNotEqual = cabinTemp.adt !== event.newData.adt || cabinTemp.cabinNo !== event.newData.cabinNo;
  if (!quote.value.isReplicate && isInvalidCruiseUpdate(data.value, event.index, event.newData, cruiseMinimumConfig.value)) {
    showMinNotification(t('advance_quote.form.min_pax_cruise_book', { minPax: cruiseMinimumConfig.value.minPax, minRoom: cruiseMinimumConfig.value.minRoomsCabins }));
  } else {
    data.value[event.index].adt = event.newData.adt;
    data.value[event.index].chd = event.newData.chd;
    data.value[event.index].cabinNo = event.newData.cabinNo;
    if (isNotEqual) {
      data.value[event.index].cabinInfos.forEach((cabinInfo: any) => {
        cabinInfo.singleNo = 0;
        cabinInfo.doubleNo = 0;
        cabinInfo.tripleNo = 0;
        cabinInfo.quadNo = 0;
        cabinInfo.fiveInRoomNo = 0;
        cabinInfo.sixInRoomNo = 0;
      });
      data.value[event.index].cabinDetailOccupancies = null;
      toast.add({ severity: 'info', summary: '', detail: t('notification.info_delete_room_entered'), life: 5000 });
    }
    submitCruiseQuote();
  }
};

const updateBookingRequestFormUltimate = () => {
  submitCruiseQuote(false, true);
};

const handleHeaderLead = () => {
  const gType = quote.value.gType;
  return gType.toUpperCase() === 'WEDDING' ? t('book_form.bride_groom') : t('book_form.group_leader');
};

onBeforeMount(() => {
  data.value = quote.value.cruises;
  data.value?.forEach((cruise: any, cruiseIndex: number) => {
    cruise.key = `Cruise${cruiseIndex}`;
    cruise.cabinInfos.forEach((cabin: any, cabinIndex: number) => {
      cabin.key = `Cabin${cruiseIndex}${cabinIndex}`;
    });
  });
});

onMounted(() => {
  data.value.forEach((element: any) => {
    expandedRows.value = { ...expandedRows.value, [element.key]: true };
  });
  cabinDetailOccupanciesClone.value = cloneDeep(data.value?.[0].cabinDetailOccupancies);
  emits('openClickSubmit', handleCheckZero(quote.value));
});
</script>
<template>
  <div class="booking-option-wrap">
    <LoadingSpinner v-if="isLoading" />
    <DataTable :value="data" v-model:expandedRows="expandedRows" dataKey="key" editMode="row" v-model:editingRows="cruiseEditingRows" @row-edit-save="onRowEditSaveCruise($event)">
      <template #header>
        <div class="grid">
          <div class="col">
            <h5>
              {{ $t('book_form.occupancy') }}
              <br />
              <small>{{ $t('book_form.quote_no') }}.: {{ quote.sk }}</small>
              <br />
              <small class="pre-line">{{ $t('book_form.booking_no') }}.: {{ formatBookingId(quote.bookingId) }}</small>
            </h5>
          </div>
          <div class="col">
            <p>
              {{ $t('advance_quote.form.group_name') }}: {{ quote.gName }}
              <br />
              {{ $t('advance_quote.form.group_type') }}: {{ quote.gType || '-' }}
              <br />
              {{ $t('advance_quote.form.group_contact') }}: {{ quote.gContact }}
            </p>
            <div class="hidden align-items-center emergency-phone-class">
              <label for="cruise-emergency-phone">{{ $t('book_form.emergency_phone') }}</label>
              <div class="col">
                <InputMask id="cruise-emergency-phone" :mask="$t('common.labels.emergency_phone_mask')" v-model="quote.agencyPhone" :placeholder="$t('common.labels.emergency_phone_number')" />
              </div>
            </div>
          </div>
          <div class="col">
            <div class="export-booking">
              <ExportExcelFile v-slot="slotProps" :fileName="renderFileNameExport(quote.sk, quote?.gName, CRUISE_TYPE)" :isAoaToSheet="true" isExportMultiple :columnsStyle="columnStyles">
                <Button icon="pi pi-external-link" @click="handleExportCruiseBooking(slotProps.export, data, isManagement, handleHeaderLead())" :label="$t('common.buttons.export_pax_list')" class="export-booking-btn" />
              </ExportExcelFile>
            </div>
          </div>
        </div>
      </template>
      <TableColumn :expander="true" class="w-3rem" />
      <TableColumn field="option" :header="$t('book_form.option')">
        <template #body="slotProps"> {{ $t('book_form.option') }} {{ slotProps.index + 1 }} </template>
      </TableColumn>
      <TableColumn field="cruiseLine" :header="$t('book_form.cruise_line')"> </TableColumn>
      <TableColumn field="ship" :header="$t('worksheet.cruise_ship')"> </TableColumn>
      <TableColumn field="sailing" :header="$t('worksheet.cruise_sailing')"> </TableColumn>
      <TableColumn field="adt" :header="$t('book_form.adults')">
        <template #editor="{ data, field }">
          <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus :useGrouping="false" />
        </template>
      </TableColumn>
      <TableColumn field="chd" :header="$t('book_form.children')">
        <template #editor="{ data, field }">
          <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus :useGrouping="false" />
        </template>
      </TableColumn>
      <TableColumn field="cabinNo" :header="$t('book_form.cabin')">
        <template #editor="{ data, field }">
          <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus :useGrouping="false" />
        </template>
      </TableColumn>
      <TableColumn v-if="isManagement" :header="$t('common.edit')" :rowEditor="true" class="py-3"></TableColumn>

      <template #expansion="slotProps">
        <div v-if="isShowModalBehind">
          <!-- cabinInfos Table -->
          <CruiseOptionCabinsInfoTable
            :cabinDetailOccupancies="cabinDetailOccupanciesClone"
            :dataCabins="slotProps"
            :cabinType="cabinType"
            :isManagement="isManagement"
            :priorityStatus="priorityStatus"
            :isShowAddPax="isShowAddPax"
            @submitCruiseQuote="submitCruiseQuote()"
            @addPax="addPax($event, true)"
          />
          <!-- preHotel Table -->
          <CruiseOptionPreHotelTable
            v-if="slotProps.data.preHotel.length"
            :dataPreHotelPax="data"
            :dataPreHotel="slotProps"
            :isManagement="isManagement"
            :priorityStatus="priorityStatus"
            :isShowAddPax="isShowAddPax"
            :isSubmit="isSubmit"
            @submitCruiseQuote="submitCruiseQuote()"
            @addPax="addPax($event)"
          />
          <!-- postHotel Table -->
          <CruiseOptionPostHotelTable
            v-if="slotProps.data.postHotel.length"
            :dataPostHotelPax="data"
            :dataPostHotel="slotProps"
            :isManagement="isManagement"
            :priorityStatus="priorityStatus"
            :isShowAddPax="isShowAddPax"
            :isSubmit="isSubmit"
            @submitCruiseQuote="submitCruiseQuote()"
            @addPax="addPax($event)"
            class="my-4"
          />
          <!-- flightPrices Table -->
          <CruiseOptionFlightPriceTable
            v-if="slotProps.data.flightPrices.length"
            :dataFlightPax="data"
            :dataFlightPrice="slotProps"
            :isManagement="isManagement"
            :priorityStatus="priorityStatus"
            @submitCruiseQuote="submitCruiseQuote()"
            @addPax="addPax($event)"
            class="mb-4"
          />
        </div>
      </template>
    </DataTable>
    <div class="flex justify-content-end mt-3">
      <Button id="update-booking-request" :label="$t('book_form.update_booking_request_form')" icon="pi pi-check" @click="updateBookingRequestFormUltimate()" />
    </div>
  </div>
</template>
<style lang="scss" scoped>
:deep(.text-center .p-column-header-content) {
  justify-content: center;
}
.pre-line {
  white-space: pre-line;
}
</style>
